import React, { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import TemplateThumbnail from "./TemplateThumbnail";
import { Search, Check } from "lucide-react";

/** Template names arrive as "07 - The Product - Product Management"; split them so people can shop by trade. */
export const describeTemplate = (name = "") => {
  const parts = name.split(" - ").map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 3 && /^\d+$/.test(parts[0])) return { number: parts[0], label: parts[1], category: parts.slice(2).join(" - ") };
  if (parts.length === 2 && /^\d+$/.test(parts[0])) return { number: parts[0], label: parts[1], category: "" };
  if (parts.length >= 2) return { number: "", label: parts[0], category: parts.slice(1).join(" - ") };
  return { number: "", label: name, category: "" };
};

const TemplateGallery = ({ open, onOpenChange, templates, value, onSelect }) => {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");

  const decorated = useMemo(() => templates.map((t) => ({ ...t, ...describeTemplate(t.name) })), [templates]);
  const categories = useMemo(() => [...new Set(decorated.map((t) => t.category).filter(Boolean))].sort(), [decorated]);
  const shown = decorated.filter((t) => {
    const hay = `${t.name} ${t.description || ""}`.toLowerCase();
    return (category === "all" || t.category === category) && (!query.trim() || hay.includes(query.trim().toLowerCase()));
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-5xl max-h-[90dvh] overflow-y-auto p-4 sm:p-6" data-testid="template-gallery">
        <DialogHeader>
          <DialogTitle>Choose your resume template</DialogTitle>
          <DialogDescription className="text-slate-500">
            {templates.length} templates available on your plan. Pick the one that suits your trade — your content stays exactly the same.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name or profession…"
              className="h-10 bg-white border-slate-200 pl-9 text-sm" data-testid="template-search" />
          </div>
          {categories.length > 0 && (
            <select value={category} onChange={(e) => setCategory(e.target.value)}
              className="h-10 rounded-md border border-slate-200 bg-white px-3 font-sans text-sm text-slate-900" data-testid="template-category-filter">
              <option value="all">All professions</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
        </div>

        <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="template-gallery-grid">
          {shown.map((t) => (
            <button key={t.id} onClick={() => { onSelect(t.id); onOpenChange(false); }}
              className={`group rounded-xl border p-2 text-left transition-colors ${value === t.id ? "border-sky-300 bg-sky-50 ring-2 ring-sky-200" : "border-slate-200 hover:border-slate-400 hover:bg-slate-50"}`}
              data-testid={`gallery-template-${t.id}`}>
              <div className="flex justify-center rounded-lg border border-slate-200 bg-white p-2 shadow-sm">
                <TemplateThumbnail tpl={t} size="lg" />
              </div>
              <p className="mt-2 flex items-start gap-1 text-xs font-semibold text-slate-900">
                {value === t.id && <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sky-600" />}
                <span className="break-words">{t.label}</span>
              </p>
              {t.category && <p className="text-[11px] text-slate-500 break-words">{t.category}</p>}
            </button>
          ))}
        </div>
        {!shown.length && <p className="py-8 text-center text-sm text-slate-500" data-testid="template-gallery-empty">No template matches that search.</p>}

        <div className="flex justify-end pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="h-10 border-slate-300 text-slate-700" data-testid="template-gallery-close">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateGallery;
